<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['toggle']);

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
const count = computed(() =>
  dynamicCount.value > 99 ? '99+' : dynamicCount.value
);
</script>

<template>
  <component
    :is="to ? 'router-link' : 'div'"
    class="relative flex items-center gap-2 px-2 py-1 rounded-xl h-8 min-w-0 overflow-hidden outline outline-1 outline-transparent border-s-2 border-transparent transition-all duration-150"
    role="button"
    draggable="false"
    :to="to"
    :title="label"
    :class="{
      'text-[#12366A] dark:text-white bg-[#DCEBFF] dark:bg-[#12366A] outline-[#2563EB]/70 border-s-[#2563EB] dark:border-s-[#D9FF00] font-semibold':
        isActive && !hasActiveChild,
      'text-[#12366A] dark:text-white bg-[#E8F1FF] dark:bg-[#0F294F] outline-[#2563EB]/55 border-s-[#2563EB]/80 font-semibold':
        hasActiveChild,
      'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-[#0F294F] hover:outline-[#2563EB]/45 hover:text-[#12366A] dark:hover:text-white':
        !isActive && !hasActiveChild,
    }"
    @click.stop="emit('toggle')"
  >
    <div v-if="icon" class="relative flex items-center gap-2">
      <Icon v-if="icon" :icon="icon" class="size-4" />
      <span
        v-if="showBadge"
        class="size-2 -top-px ltr:-right-px rtl:-left-px bg-[#2563EB] dark:bg-[#D9FF00] absolute rounded-full border border-white dark:border-slate-950"
      />
    </div>
    <div
      class="flex items-center gap-1.5 flex-grow justify-between min-w-0 flex-1"
    >
      <span
        class="truncate"
        :class="{
          'text-body-main': !isActive,
          'font-medium text-sm': isActive || hasActiveChild,
        }"
      >
        {{ label }}
      </span>
      <span
        v-if="dynamicCount && !expandable"
        class="inline-grid h-5 min-w-5 place-items-center rounded-full bg-[#2563EB]/15 dark:bg-[#2563EB]/35 px-1 text-xxs font-semibold leading-3 text-[#12366A] dark:text-white flex-shrink-0"
      >
        {{ count }}
      </span>
    </div>
    <span
      v-if="expandable"
      v-show="isExpanded"
      class="i-lucide-chevron-up size-3"
      @click.stop="emit('toggle')"
    />
  </component>
</template>
